//#region libraries
// react
import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";

// bootstrap
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";

// custom components
import { Card, CustomSpinner } from "_atoms";
import { Clicker, CalendarTile, DepPaxNoShowsToggle, SelectedAirportCard } from "_molecules";
import { BookingForecast, AirlineAirportSelector, WeatherTile, BookingTotals } from "_organisms";

// public libraries
import { datesGenerator } from "dates-generator";
import { Text } from "@tremor/react";
import { format } from "date-fns";

// redux
import { selectAirlineSelectedAirport } from "_redux/slices/prm-bookings/bookings";
import { selectFirebaseInfo, selectIata } from "_redux/slices/main-screen/user";
import {
  getBookingsCountInbound, getBookingsCountOutbound, getBookingsForecastCountInbound, getBookingsForecastCountOutbound, selectGetBookingForecastOutboundStatus,
  selectGetBookingForecastInboundStatus, selectGetBookingOutboundStatus, selectGetBookingInboundStatus,

} from "_redux/slices/prm-bookings/bookings-count";
import { selectLowestAndHighestDay, rankDaysOfWeekByBookings } from "_redux/slices/prm-bookings/bookings-count";
import { setDate, selectDepPaxNoShowsFilter, } from "_redux/slices/prm-bookings/calendar";
import { getSeasonalVariance } from '_redux/slices/prm-bookings/seasonal-variance';

// styling
import { Colors, Spacing } from "_styles";

// utils
import { useIsMobile } from "_utils/hooks";

// date time functions
import { days, months, getMonthString } from '../../../utils/date-time-functions';
import { SeasonalVarianceToggle } from "../../molecules";

//#endregion

const Calendar = (props) => {

  //#region states

  // calendar variables
  const [airportSelector, setAirportSelector] = useState(false);            // selected date in calendar
  const [selector, setSelector] = useState(false);
  const [dates, setDates] = useState([]);                                   // hold all dates for chosen month
  const [calendar, setCalendar] = useState({ month: new Date().getMonth(), year: new Date().getFullYear() });
  const [calendarLoading, setCalendarLoading] = useState(true);
  const [intialRender, setInitalRender] = useState(true);
  const [disabledDate, setDisabledDate] = useState(false);                  // true if the navigated date is more than 12 month ago
  //#endregion

  //#region redux
  const dispatch = useDispatch();

  // user info
  const dbUser = useSelector(selectFirebaseInfo);
  const iata = useSelector(selectIata);
  const airport = useSelector(selectAirlineSelectedAirport);

  // highlights
  const leastAndHighestDay = useSelector(selectLowestAndHighestDay(new Date(calendar.year, calendar.month)));
  const orderedDays = useSelector(rankDaysOfWeekByBookings(new Date(calendar.year, calendar.month)));                // list of dates in week from least busy to most busy

  // dep pax no shows filter
  const depPaxNoShowsFilter = useSelector(selectDepPaxNoShowsFilter);

  // last request timestamp for calendar bookings
  // const lastCalendarRefresh = useSelector(selectCalendarRefreshTimeStamp)
  //const lastCalendarRefreshMonth = useSelector(selectCalendarRefreshMonth)

  // status flags
  const bookingForecastInboundStatus = useSelector(selectGetBookingForecastInboundStatus);
  const bookingForecastOutboundStatus = useSelector(selectGetBookingForecastOutboundStatus);
  const bookingInboundStatus = useSelector(selectGetBookingInboundStatus);
  const bookingOutboundStatus = useSelector(selectGetBookingOutboundStatus);

  let controller = new AbortController();

  // phones get a vertical list of days instead of the 7-column month grid
  const isMobile = useIsMobile();

  //#endregion

  //#region use effects  

  // initalize calendar
  useEffect(() => {
    const calendar = {
      month: new Date().getMonth(),
      year: new Date().getFullYear(),
    };

    // reset calendar
    const body = {
      month: calendar.month,
      year: calendar.year,
    };

    // get dates for month datesgenerator(current month, year)
    const { dates, nextMonth, nextYear, previousMonth, previousYear } =
      datesGenerator(body);
    setDates([...dates]);

    setCalendar({
      ...calendar,
      nextMonth,
      nextYear,
      previousMonth,
      previousYear,
    });
  }, [iata, airport]);

  // set current date
  useEffect(() => {
    // set weather tile
    let todayIso = format(new Date(), "yyyy-MM-dd");
    dispatch(setDate(todayIso));
    setDisabledDate(false);

  }, []);

  // get seasonal variances
  useEffect(() => {
    if (dbUser && dbUser.details.type === "AIRPORT") {
      dispatch(getSeasonalVariance(dbUser.details.authority))
    }
  }, []);

  // dep pax no show 
  useEffect(() => {
    // make sure it is not first render calling use effect
    if (depPaxNoShowsFilter && !intialRender) {
      // need to get the bookings for the month and exclude the no shows
      console.log('calling here...')
      refreshBookings();
    } else if (!intialRender) {
      // need to get the bookings for the month and include the no shows
      console.log('calling here!')
      refreshBookings();
    }
  }, [depPaxNoShowsFilter]);

  // if calendar dates change refresh bookings using new dates
  useEffect(() => {
    // // check if bookings were refreshed in the last 10 minutes
    // let currentTime = new Date();
    // let differenceInMillis = lastCalendarRefresh - currentTime;

    // get current Date 
    let currentDate = new Date(new Date().getTime())

    // add 10 minutes to the last Calendar Refresh TimeStamp
    // let lastCalendarRefreshPlusTen = new Date(new Date(lastCalendarRefresh).getTime() + 10 * 60000)

    refreshBookings()

    // compare between currentDate and last Calendar Refresh after adding ten minutes to it if the currentDate is passed the last calender refresh 
    // after adding ten minutes to it that means the last refresh was more than ten minutes ago and it will refresh 
    //if(currentDate > lastCalendarRefreshPlusTen) { 
    //refreshBookings()
    //}



  }, []);

  //#endregion

  //#region month clicker



  const refreshBookings = (selectedYear) => {

    console.log('10 mins since last refresh, refreshing again...')
    let year = calendar.year;
    if (selectedYear !== null && selectedYear !== undefined)
      year = selectedYear;


    let countParams = {
      iata:
        dbUser.details.type == "AIRLINE"
          ? airport
          : dbUser.details.type == "TRAVEL_AGENT"
            ? "TRAVEL_AGENT"
            : dbUser.details.authority,

      date: year + "-" + (parseInt(calendar.month) + 1),
      airline:
        dbUser.details.type == "AIRLINE" ? dbUser.details.authority : null,

    };
    const outboundCountParams = {
      ...countParams,
      noShows: depPaxNoShowsFilter,
    }
    let signal = controller.signal;
    dispatch(getBookingsCountInbound(countParams, signal));
    dispatch(getBookingsCountOutbound(outboundCountParams, signal));
    if (dbUser.details.type !== "AIRLINE") {
      dispatch(getBookingsForecastCountInbound(countParams, controller.signal));
      dispatch(getBookingsForecastCountOutbound(outboundCountParams, controller.signal));
    }

  };

  //this function will be called for airline account, and when airport is updated
  const refreshCalendarAfterChangeAirport = () => {

    if (dbUser.details.type === "AIRLINE" && airportSelector) {
      setAirportSelector(false);
      let year = calendar.year;

      let countParams = {
        iata: airport,
        date: year + "-" + (parseInt(calendar.month) + 1),
        airline: dbUser.details.authority,
      };
      let signal = controller.signal;
      dispatch(getBookingsCountInbound(countParams, signal));
      dispatch(getBookingsCountOutbound(countParams, signal));

    }
  }


  const onClickNext = () => {
    controller.abort();
    setDisabledDate(false);

    // get next month date info
    const body = { month: calendar.nextMonth, year: calendar.nextYear };
    const { dates, nextMonth, nextYear, previousMonth, previousYear } =
      datesGenerator(body);
    // set info as next month
    setDates([...dates]);
    setCalendar({
      ...calendar,
      month: calendar.nextMonth,
      year: calendar.nextYear,
      nextMonth,
      nextYear,
      previousMonth,
      previousYear,
    });

    let countParams = {};
    countParams = {
      iata: dbUser.details.type != "AIRLINE" ? iata : airport,
      date: calendar.nextYear + "-" + parseInt(calendar.nextMonth + 1),
      airline:
        dbUser.details.type == "AIRLINE" ? dbUser.details.authority : null,

    };
    const outboundCountParams = {
      ...countParams,
      noShows: depPaxNoShowsFilter,
    }

    dispatch(getBookingsCountInbound(countParams, controller.signal));
    dispatch(getBookingsCountOutbound(outboundCountParams, controller.signal));

    if (dbUser.details.type !== "AIRLINE") {
      dispatch(getBookingsForecastCountInbound(countParams, controller.signal));
      dispatch(getBookingsForecastCountOutbound(outboundCountParams, controller.signal));
    }

  };

  const onClickPrevious = () => {
    controller.abort();
    // get previous month date info
    const body = { month: calendar.previousMonth, year: calendar.previousYear };
    const { dates, nextMonth, nextYear, previousMonth, previousYear } =
      datesGenerator(body);

    // get the current date and calculate the date 12 months ago
    const currentDate = new Date();
    const twelveMonthsAgo = new Date();
    twelveMonthsAgo.setMonth(currentDate.getMonth() - 12);

    //gGet the target date (previous month)
    const targetDate = new Date(calendar.previousYear, calendar.previousMonth, 1);

    // check if the target date is more than 12 months in the past
    if (targetDate < twelveMonthsAgo) {
      setDisabledDate(true);
      console.log("can't go back more than 12 months from the current date.");
      return;
    }
    setDisabledDate(false);

    // set info as next month
    setDates([...dates]);
    setCalendar({
      ...calendar,
      month: calendar.previousMonth,
      year: calendar.previousYear,
      nextMonth,
      nextYear,
      previousMonth,
      previousYear,
    });

    let countParams = {};

    countParams = {
      iata: dbUser.details.type != "AIRLINE" ? iata : airport,
      date: calendar.previousYear + "-" + parseInt(calendar.previousMonth + 1),
      airline:
        dbUser.details.type == "AIRLINE" ? dbUser.details.authority : null,

    };

    const outboundCountParams = {
      ...countParams,
      noShows: depPaxNoShowsFilter,
    }


    dispatch(getBookingsCountInbound(countParams, controller.signal));
    dispatch(getBookingsCountOutbound(outboundCountParams, controller.signal));
    if (dbUser.details.type !== "AIRLINE") {
      dispatch(getBookingsForecastCountInbound(countParams, controller.signal));
      dispatch(getBookingsForecastCountOutbound(outboundCountParams, controller.signal));
    }
  };

  //#endregion

  // loading listener
  useEffect(() => {
    if (bookingForecastInboundStatus == 'loading' || bookingForecastOutboundStatus == 'loading' || bookingInboundStatus == 'loading' || bookingOutboundStatus == 'loading') {
      setCalendarLoading(true)
    }
    else {
      setCalendarLoading(false)
    }
  }, [bookingForecastInboundStatus, bookingForecastOutboundStatus, bookingInboundStatus, bookingOutboundStatus]);

  useEffect(() => {
    setInitalRender(false);
  }, []);



  return (
    <div style={{ marginTop: Spacing.SCALE_22 }}>

      {/* calendar top cards */}

      <Row >

        {/* Total Booking Count */}
        <Col xs={12} sm={6} lg={3} className="mb-2">
          <BookingTotals
            total={true}
            calendar={""}
            title={"Total Bookings"}
            subtitle={getMonthString(calendar.month) + " " + calendar.year}
          />
        </Col>
        {/* Total forecast Count */}
        <Col xs={12} sm={6} lg={3} className="mb-2">
          {dbUser.details.type !== "AIRLINE" &&
            <BookingForecast
              total={true}
              calendar={""}
              title={"Total Forecasted Bookings"}
              subtitle={getMonthString(calendar.month) + " " + calendar.year}
            />
          }
        </Col>



        {/* Passenger Pre Notification Count 
          <Col>
            {dbUser.details.type != 'TRAVEL_AGENT' && dbUser.details.type != 'AIRLINE' && <PrenotifiedChart calendar={calendar} />}
          </Col>
          */}

        {/* Weather Tile 
          {dbUser.details.type == "TRAVEL_AGENT" || dbUser.details.type == "AIRLINE" ? ("") : (
            <Col xs={3}>
              <WeatherTile />
            </Col>
          )}*/}

        {/* Airport Selector for Airline */}
        {dbUser.details.type == "AIRLINE" &&
          <Col xs={12} sm={6} lg={2} className="mb-2">
            <SelectedAirportCard setAirportSelector={(val) => { setAirportSelector(val) }} setSelector={(val) => { setSelector(val) }} />
          </Col>
        }

      </Row>


      {/* airport selector */}
      {airportSelector && <Row><AirlineAirportSelector edit={selector} close={refreshCalendarAfterChangeAirport} /></Row>}

      {/* calendar */}
      <Card className='mt-2'>
        <div style={{ maxWidth: "100%", overflowX: "auto" }}>

          {/* calendar toolbar */}
          <Row>
            <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: isMobile ? 'center' : 'space-between', alignItems: 'center', rowGap: Spacing.SCALE_12, width: '100%' }}>

              {/* title + loader */}
              {dbUser.details.type === "AIRPORT" ? <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center' }}>
                <DepPaxNoShowsToggle />
                <div style={{ marginLeft: Spacing.SCALE_22 }}>
                  <SeasonalVarianceToggle />
                </div>
                <div style={{ marginLeft: Spacing.SCALE_12 }}> {calendarLoading && <CustomSpinner size={'small'} style={{ marginTop: -Spacing.SCALE_18 }} />}</div>
              </div> : <div></div>}

              {/* date selector */}
              <Clicker
                onClickPrevious={onClickPrevious}
                onClickNext={onClickNext}
                text={months[calendar.month] + " " + calendar.year}
                disabledDate={disabledDate}
              />
            </div>
          </Row>

          {isMobile ? (
            // one full-width tile per day of the selected month
            <div style={{ marginTop: Spacing.SCALE_12 }}>
              {dates.flat().filter((each) => each.month === calendar.month).map((each) => (
                <CalendarTile
                  key={JSON.stringify(each)}
                  fluid={true}
                  orderedDays={orderedDays}
                  leastAndHighestDay={leastAndHighestDay}
                  calendarMonth={calendar.month + 1}
                  date={each.date}
                  month={each.month}
                  year={calendar.year}
                  isLoading={false}
                />
              ))}
            </div>
          ) : (
          <table>

            {/* render days of week on top of dates */}
            <tbody style={{}}>
              <tr>
                {days.map((day) => (
                  <td key={day} style={{}}>
                    <div style={{ textAlign: "center", height: Spacing.SCALE_32 }}>
                      <p style={{ fontSize: 16, fontWeight: "lighter", color: Colors.TEXT_COLOR }}>{day}</p>
                    </div>
                  </td>
                ))}
              </tr>

              {/* render dates under days of week */}
              {dates.length > 0 && dates.map((week) => (
                <tr key={JSON.stringify(week[0])}>
                  {week.map((each, index) => (
                    <td key={JSON.stringify(each)}>
                      <CalendarTile
                        orderedDays={orderedDays}
                        leastAndHighestDay={leastAndHighestDay}
                        calendarMonth={calendar.month + 1}
                        date={each.date}
                        month={each.month}
                        year={calendar.year}
                        isLoading={false}
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          )}

        </div>
      </Card>

      {/* spacer */}
      <div style={{ marginTop: Spacing.SCALE_22, marginBottom: Spacing.SCALE_22 }} />


    </div>
  );
};

export default Calendar;